import { useId, useRef } from 'react';
import { useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Card from '@rio-cloud/rio-uikit/Card';
import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';
import { getSupportedLocale } from '../../configuration/lang/lang';
import { useUserProfile } from '../../configuration/login/loginSlice';
import { gaPush } from '../../configuration/setup/googleAnalytics';
import { useAdvertisementSeen } from '../../hooks/useAdvertisementSeen';
import type { DriverChatCampaign } from '../../hooks/useGetUpsellingCampaign';
import illustration from '../../assets/driver-chat-placeholder.svg';
import messagesDE from '../translations/de-DE.json';
import messagesEN from '../translations/en-GB.json';

type MessageName =
    | 'intro'
    | 'communication.title'
    | 'communication.description'
    | 'orders.title'
    | 'orders.description'
    | 'compact.intro'
    | 'compact.booking'
    | 'cta';

export const DriverChatUpsellingView = ({ campaign }: { campaign: DriverChatCampaign }) => {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useOnScreen(ref);
    const titleId = useId();
    const intl = useIntl();
    const userProfile = useUserProfile();
    const preferredLocale = userProfile?.locale ?? intl.locale;
    const isGerman = preferredLocale.toLowerCase().split(/[-_]/)[0] === 'de';
    const linkLanguage = isGerman ? 'de' : getSupportedLocale(intl.locale).split('-')[0];
    const messages = isGerman ? messagesDE : intl.messages;
    const isCompact = campaign.layout === 'compact';
    const message = (name: MessageName) => {
        const id = `intl-msg:chat.driverChatUpsell.${name}` as const;
        const translation = messages[id];
        const hasTranslation = typeof translation === 'string' && translation.trim();
        return (
            <span lang={hasTranslation ? (isGerman ? 'de' : intl.locale) : 'en'}>
                {hasTranslation ? translation : messagesEN[id]}
            </span>
        );
    };

    useAdvertisementSeen(inView);

    const cta = (
        <Button
            as='a'
            href={`https://rio.cloud/${linkLanguage}/marketplace/produkt/order-communication`}
            target='_blank'
            rel='noopener noreferrer'
            bsStyle='primary'
            bsSize='lg'
            block
            multiline
            onClick={() => {
                gaPush({
                    category: campaign.trackingCategory,
                    action: 'Learn more',
                    label: 'Clicked learn more link in upselling advertisement',
                });
            }}
        >
            {message('cta')}
        </Button>
    );

    return (
        <div
            ref={ref}
            className={`height-100pct overflow-y-auto bg-lightest ${isCompact ? 'padding-25' : 'padding-20'}`}
        >
            <Card
                className={`${isCompact ? 'max-width-350' : 'max-width-400'} margin-auto`}
                padding={25}
                shadow='subtle'
            >
                <section
                    aria-labelledby={titleId}
                    className={isCompact ? 'display-grid gap-25 text-center' : 'display-flex flex-column gap-25'}
                >
                    <header className={isCompact ? 'display-grid gap-10 justify-content-center' : 'text-center'}>
                        {isCompact ? (
                            <span
                                className='rioglyph rioglyph-chat text-size-h1 text-color-primary'
                                aria-hidden='true'
                            />
                        ) : (
                            <img
                                src={illustration}
                                alt=''
                                width={280}
                                height={130}
                                className='max-width-100pct height-auto margin-bottom-20'
                            />
                        )}
                        <h1
                            id={titleId}
                            className={
                                isCompact
                                    ? 'text-size-h1 text-medium text-color-darkest line-height-125rel text-wrap-balance margin-0'
                                    : 'text-size-h2 text-color-darkest margin-0'
                            }
                            style={{ textTransform: 'none' }}
                        >
                            {isCompact ? 'Driver chat' : 'Driver Chat'}
                        </h1>
                    </header>
                    {isCompact ? (
                        <div className='display-grid gap-25 justify-content-center'>
                            <p className='text-size-18 text-color-dark line-height-125rel text-wrap-balance margin-0'>
                                {message('compact.intro')}
                            </p>
                            <p className='text-size-18 text-color-dark line-height-125rel text-wrap-balance margin-0'>
                                {message('compact.booking')}
                            </p>
                        </div>
                    ) : (
                        <div className='display-flex flex-column gap-20'>
                            <p className='text-center text-size-16 text-color-dark margin-0'>{message('intro')}</p>
                            <div className='bg-lightest rounded padding-20 display-flex gap-15'>
                                <span
                                    className='rioglyph rioglyph-chat text-size-h3 text-color-primary'
                                    aria-hidden='true'
                                />
                                <div>
                                    <h2 className='text-size-16 text-medium text-color-darkest margin-top-0 margin-bottom-10'>
                                        {message('communication.title')}
                                    </h2>
                                    <p className='text-color-dark margin-0'>{message('communication.description')}</p>
                                </div>
                            </div>
                            <div className='bg-lightest rounded padding-20 display-flex gap-15'>
                                <span
                                    className='rioglyph rioglyph-truck text-size-h3 text-color-primary'
                                    aria-hidden='true'
                                />
                                <div>
                                    <h2 className='text-size-16 text-medium text-color-darkest margin-top-0 margin-bottom-10'>
                                        {message('orders.title')}
                                    </h2>
                                    <p className='text-color-dark margin-0'>{message('orders.description')}</p>
                                </div>
                            </div>
                            {cta}
                        </div>
                    )}
                    {isCompact && cta}
                </section>
            </Card>
        </div>
    );
};
